import api from './api'

// Controlled "TB Assistant" — a retrieval-based (NOT generative) assistant over
// a curated, agenda-scoped knowledge base. Answers come from admin-authored
// topics that reference real official sources (Central TB Division / Ni-Kshay).
// The assistant never diagnoses; emergencies/diagnosis are routed to a qualified
// healthcare professional via the emergencyNote field.

export type AssistantTopicCategory =
  | 'GENERAL_INFO' | 'SYMPTOMS' | 'TESTING' | 'TREATMENT'
  | 'SUPPORT' | 'NI_KSHAY_MITRA' | 'FACILITIES' | 'EMERGENCY'

export type ResourceStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

export const ASSISTANT_CATEGORY_KEYS: AssistantTopicCategory[] = [
  'GENERAL_INFO', 'SYMPTOMS', 'TESTING', 'TREATMENT',
  'SUPPORT', 'NI_KSHAY_MITRA', 'FACILITIES', 'EMERGENCY',
]

export const ASSISTANT_CATEGORY_LABELS: Record<AssistantTopicCategory, string> = {
  GENERAL_INFO: 'General info',
  SYMPTOMS: 'Symptoms',
  TESTING: 'Testing',
  TREATMENT: 'Treatment',
  SUPPORT: 'Support',
  NI_KSHAY_MITRA: 'Ni-Kshay Mitra',
  FACILITIES: 'Find a centre',
  EMERGENCY: 'Emergency',
}

export const ASSISTANT_STATUS_LABELS: Record<ResourceStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
}

export interface AssistantSuggestion {
  id: number
  title: string
  category: AssistantTopicCategory
  slug: string
}

export interface AssistantAnswer {
  matched: boolean
  query: string
  topicId?: number
  topicTitle?: string
  category?: AssistantTopicCategory
  answer: string
  officialSourceLabel?: string
  officialSourceUrl?: string
  portalLinkLabel?: string
  portalLinkPath?: string
  suggestions?: AssistantSuggestion[]
  disclaimer: string
  emergencyNote?: string | null
}

export interface AssistantTopic {
  id: number
  title: string
  slug: string
  answer: string
  triggerPhrases?: string
  keywords?: string
  category: AssistantTopicCategory
  officialSourceLabel?: string
  officialSourceUrl?: string
  portalLinkLabel?: string
  portalLinkPath?: string
  sortOrder?: number
  status?: ResourceStatus
  createdAt?: string
  updatedAt?: string
}

export interface AssistantTopicRequest {
  title: string
  slug?: string
  answer: string
  triggerPhrases?: string
  keywords?: string
  category: AssistantTopicCategory
  officialSourceLabel?: string
  officialSourceUrl?: string
  portalLinkLabel?: string
  portalLinkPath?: string
  sortOrder?: number
  status?: ResourceStatus
}

export interface AssistantStats {
  TOTAL: number
  DRAFT: number
  PUBLISHED: number
  ARCHIVED: number
}

export interface AssistantTopicPage {
  content: AssistantTopic[]
  totalElements: number
  totalPages: number
}

/** Ask the controlled TB Assistant a question (public). */
export async function askAssistant(query: string): Promise<AssistantAnswer> {
  const res = await api.post('/assistant/ask', { query })
  return res.data?.data ?? res.data
}

/** Public published topic list (for suggestion chips). */
export async function getAssistantTopics(): Promise<AssistantTopic[]> {
  try {
    const res = await api.get('/assistant/topics')
    return res.data?.data ?? []
  } catch (error) {
    console.error('Failed to fetch assistant topics:', error)
    return []
  }
}

export async function getAssistantTopicBySlug(slug: string): Promise<AssistantTopic> {
  const res = await api.get(`/assistant/topics/${slug}`)
  return res.data?.data ?? res.data
}

// ---- admin ----
export async function listAssistantTopicsAdmin(params: { status?: string; category?: string; q?: string; page?: number; size?: number } = {}): Promise<AssistantTopicPage> {
  const res = await api.get('/assistant/admin', { params: { status: params.status, category: params.category, q: params.q, page: params.page ?? 0, size: params.size ?? 20 } })
  const d = res.data?.data ?? res.data
  return { content: d.content ?? [], totalElements: d.totalElements ?? 0, totalPages: d.totalPages ?? 0 }
}

export async function getAssistantStats(): Promise<AssistantStats> {
  const res = await api.get('/assistant/stats')
  return res.data?.data ?? res.data
}

export async function createAssistantTopic(req: AssistantTopicRequest): Promise<AssistantTopic> {
  const res = await api.post('/assistant', req)
  return res.data?.data ?? res.data
}

export async function updateAssistantTopic(id: number, req: AssistantTopicRequest): Promise<AssistantTopic> {
  const res = await api.put(`/assistant/${id}`, req)
  return res.data?.data ?? res.data
}

export async function changeAssistantTopicStatus(id: number, status: ResourceStatus): Promise<AssistantTopic> {
  const res = await api.put(`/assistant/${id}/status`, null, { params: { status } })
  return res.data?.data ?? res.data
}

export async function deleteAssistantTopic(id: number): Promise<void> {
  await api.delete(`/assistant/${id}`)
}

// Default object (kept for any `import assistantService` usage).
const assistantService = { askAssistant, getAssistantTopics }
export default assistantService